import { usePersistedState } from "@/lib/persistedFilters";
// Iter 108l — OAuth Tracer (internal app).
//
// Surfaced on the dashboard as "OAuth Tracer" (replaces the old
// "Julian Test" placeholder). Renders every OAuth/OIDC trace from
// `db.system_logs` where `source_kind = "oauth"`, across every
// Conduit-linked app (Training, Quotes, …) and every action
// (authorize / token_request / token_issued / userinfo / iframe_status /
// wire_in / wire_out / talentlms:*).
//
// UNAUTHENTICATED route — any operator can watch OAuth traffic live.
// Sensitive material (bearer tokens, cookies, client secrets) is
// redacted at write-time by the backend before it reaches this page.

import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { ChevronRight, Pause, Play, Trash2, ArrowDownCircle, Activity, Loader2, Radar } from "lucide-react";

const POLL_MS = 2000;

/* eslint-disable react-hooks/exhaustive-deps */

function levelPill(lvl) {
  const map = {
    debug:    "bg-slate-100 text-slate-600 border-slate-300",
    info:     "bg-sky-100 text-sky-800 border-sky-300",
    warning:  "bg-amber-100 text-amber-800 border-amber-300",
    error:    "bg-red-100 text-red-800 border-red-300",
    critical: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
  };
  return `inline-flex items-center rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${map[lvl] || map.info}`;
}

function actionColor(action) {
  if (action === "wire_in") return "text-emerald-600";
  if (action === "wire_out") return "text-indigo-600";
  if (action?.startsWith("talentlms:")) return "text-amber-700";
  if (action?.startsWith("peer:")) return "text-orange-600";
  if (action === "peer_probe") return "text-violet-700";
  if (action === "authorize" || action === "token_issued" || action === "userinfo") return "text-fuchsia-700";
  if (action === "iframe_status") return "text-cyan-700";
  return "text-slate-700";
}

function actionArrow(action) {
  // Green ← incoming to Conduit; Blue → outgoing response; Orange ↔ TalentLMS
  if (action === "wire_in")  return "→ Conduit";
  if (action === "wire_out") return "← Conduit";
  if (action?.startsWith("talentlms:")) return "↔ TalentLMS";
  if (action === "peer:conduit") return "⇢ self (probe)";
  if (action?.startsWith("peer:")) return "⇢ Third party";
  if (action === "peer_probe") return "◈ Probe result";
  if (action === "iframe_status") return "◇ Browser";
  return action || "";
}

function short(s, n = 220) {
  if (s == null) return "";
  const str = typeof s === "string" ? s : JSON.stringify(s);
  return str.length > n ? str.slice(0, n) + "…" : str;
}

function TimestampCol({ ts }) {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  const ms = String(d.getMilliseconds()).padStart(3, "0");
  return (
    <span className="font-mono text-[11px] text-slate-500">
      {hh}:{mm}:{ss}.{ms}
    </span>
  );
}

function ExpandedRow({ row }) {
  const ctx = row.context || {};
  const isWire = row.action === "wire_in" || row.action === "wire_out";
  const isTLMS = (row.action || "").startsWith("talentlms:");
  const isPeer = (row.action || "").startsWith("peer:");
  return (
    <div className="mt-1 rounded border border-slate-200 bg-slate-50 p-3 font-mono text-[11px] leading-relaxed">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Request</div>
          {isPeer && (
            <>
              <div><span className="text-slate-500">{ctx.method}</span> {ctx.url}</div>
              <div><span className="text-slate-500">peer host</span>: {ctx.peer_host || "—"}</div>
              <div><span className="text-slate-500">headers</span>:</div>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx.req_headers || {}, null, 2)}</pre>
              {ctx.request != null && (
                <>
                  <div className="mt-1"><span className="text-slate-500">body</span>:</div>
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{typeof ctx.request === "string" ? ctx.request : JSON.stringify(ctx.request, null, 2)}</pre>
                </>
              )}
            </>
          )}
          {isWire && (
            <>
              <div><span className="text-slate-500">method</span>: {ctx.method} {ctx.path}</div>
              {Object.keys(ctx.query || {}).length > 0 && (
                <div><span className="text-slate-500">query</span>: {short(ctx.query)}</div>
              )}
              <div><span className="text-slate-500">headers</span>:</div>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx.req_headers || {}, null, 2)}</pre>
            </>
          )}
          {isTLMS && (
            <>
              <div><span className="text-slate-500">url</span>: {ctx.url}</div>
              <div><span className="text-slate-500">method</span>: {ctx.method}</div>
              {ctx.request && (
                <>
                  <div className="mt-1"><span className="text-slate-500">body</span>:</div>
                  <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx.request, null, 2)}</pre>
                </>
              )}
            </>
          )}
          {!isWire && !isTLMS && !isPeer && (
            <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx, null, 2)}</pre>
          )}
        </div>
        <div>
          <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Response</div>
          {isPeer && (
            <>
              <div>
                <span className="text-slate-500">status</span>:{" "}
                <strong className={ctx.error || ctx.status_code >= 400 ? "text-red-600" : "text-emerald-700"}>
                  {ctx.error ? "TRANSPORT ERROR" : ctx.status_code}
                </strong>{" "}
                · {ctx.duration_ms}ms
              </div>
              {ctx.error && <div className="text-red-700">error: {ctx.error}</div>}
              {ctx.oidc_error && <div className="text-red-700">oidc error: {ctx.oidc_error}</div>}
              {ctx.location && <div className="break-all"><span className="text-slate-500">location</span>: {ctx.location}</div>}
              <div><span className="text-slate-500">headers</span>:</div>
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx.resp_headers || {}, null, 2)}</pre>
              {ctx.resp_body != null && (
                <>
                  <div className="mt-1"><span className="text-slate-500">body</span> (up to 20 KB):</div>
                  <pre className="mt-1 max-h-72 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{typeof ctx.resp_body === "string" ? ctx.resp_body : JSON.stringify(ctx.resp_body, null, 2)}</pre>
                </>
              )}
            </>
          )}
          {isWire && row.action === "wire_out" && (
            <>
              <div><span className="text-slate-500">status</span>: <strong className={ctx.status_code >= 400 ? "text-red-600" : "text-emerald-700"}>{ctx.status_code}</strong> · {ctx.duration_ms}ms</div>
              {ctx.oidc_error && <div className="text-red-700">oidc error: {ctx.oidc_error}</div>}
              {ctx.location && <div className="break-all"><span className="text-slate-500">location</span>: {ctx.location}</div>}
              <div><span className="text-slate-500">headers</span>:</div>
              <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{JSON.stringify(ctx.resp_headers || {}, null, 2)}</pre>
              {ctx.resp_body != null && (
                <>
                  <div className="mt-1"><span className="text-slate-500">body</span>:</div>
                  <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{typeof ctx.resp_body === "string" ? ctx.resp_body : JSON.stringify(ctx.resp_body, null, 2)}</pre>
                </>
              )}
            </>
          )}
          {isTLMS && (
            <>
              <div><span className="text-slate-500">status</span>: <strong className={ctx.status_code >= 400 ? "text-red-600" : "text-emerald-700"}>{ctx.status_code}</strong></div>
              {ctx.error && <div className="text-red-700">error: {ctx.error}</div>}
              {ctx.response != null && (
                <>
                  <div className="mt-1"><span className="text-slate-500">body</span>:</div>
                  <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10.5px]">{typeof ctx.response === "string" ? ctx.response : JSON.stringify(ctx.response, null, 2)}</pre>
                </>
              )}
            </>
          )}
          {ctx.exception && (
            <>
              <div className="mt-1 text-red-700">unhandled exception:</div>
              <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap rounded bg-red-50 p-2 text-[10.5px] text-red-800">{ctx.exception}</pre>
            </>
          )}
          {ctx.masqueraded_by && (
            <div className="mt-2 rounded bg-amber-50 p-1.5 text-amber-800">
              masqueraded by: {ctx.masqueraded_by.name || ctx.masqueraded_by.email}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ProbeBar() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/oauth/admin/clients");
        const withStart = (data.clients || []).filter((c) => (c.start_url || "").trim());
        setClients(withStart);
        const tlms = withStart.find((c) => /talentlms/i.test(c.start_url || "")) || withStart[0];
        if (tlms) setClientId(tlms.client_id);
      } catch (e) {
        setClients([]);
      }
    })();
  }, []);

  const run = async () => {
    setRunning(true); setError(""); setResult(null);
    try {
      const { data } = await api.post("/oauth/admin/peer-probe", { client_id: clientId });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "probe failed");
    } finally {
      setRunning(false);
    }
  };

  if (clients.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg border border-violet-200 bg-violet-50/60 p-3" data-testid="oauth-probe-bar">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-violet-800">Round-trip probe</span>
        <select
          data-testid="oauth-probe-client"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="rounded border border-violet-300 bg-white px-2 py-1.5 text-sm"
        >
          {clients.map((c) => (
            <option key={c.client_id} value={c.client_id}>{c.name} — {c.start_url}</option>
          ))}
        </select>
        <button
          data-testid="oauth-probe-run"
          onClick={run}
          disabled={running || !clientId}
          className="inline-flex items-center gap-1 rounded bg-violet-700 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
        >
          {running ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Walking the handshake…</> : <><Radar className="h-3.5 w-3.5" /> Run probe</>}
        </button>
        <span className="text-[11px] text-violet-900/70">
          Conduit follows the peer&apos;s OIDC dance server-side and logs every response — including the peer&apos;s own error pages.
        </span>
      </div>
      {error && <div className="mt-2 rounded bg-red-100 px-2 py-1 text-xs text-red-800" data-testid="oauth-probe-error">{error}</div>}
      {result && (
        <div className="mt-2 rounded border border-violet-200 bg-white p-2 font-mono text-[11px]" data-testid="oauth-probe-result">
          <div className="mb-1">
            verdict: <strong className={result.ok ? "text-emerald-700" : "text-red-700"}>{result.verdict}</strong>
            {" · "}{result.hops?.length || 0} hop(s)
          </div>
          {result.peer_message && (
            <div className="mb-1 rounded bg-red-50 px-2 py-1 text-red-800" data-testid="oauth-probe-peer-message">
              peer says: {result.peer_message}
            </div>
          )}
          <ol className="space-y-1">
            {(result.hops || []).map((h) => (
              <li key={h.hop} className="border-t border-slate-100 pt-1">
                <span className="text-slate-500">#{h.hop}</span>{" "}
                <span className={h.is_conduit ? "text-indigo-700" : "text-orange-700"}>{h.host}</span>{" "}
                <strong className={h.error || h.status >= 400 ? "text-red-600" : "text-emerald-700"}>{h.error ? "ERR" : h.status}</strong>
                {h.oidc_error && <span className="text-red-700"> · {h.oidc_error}</span>}
                {h.peer_message && <div className="text-red-700">peer says: {h.peer_message}</div>}
                {h.note && <span className="text-amber-700"> · {h.note}</span>}
                <div className="truncate text-slate-500">{h.url}</div>
                {h.error && <div className="text-red-700">{h.error}</div>}
                {h.body_preview && (
                  <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap rounded bg-slate-50 p-2 text-[10.5px]">{h.body_preview}</pre>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export function OAuthTracerPanel() {
  const [rows, setRows] = useState([]);
  const [paused, setPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [filter, setFilter] = usePersistedState("oauth-debug.filter", "");
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [minutes, setMinutes] = useState(30);
  const seenIds = useRef(new Set());   // dedupe on ts+action+summary
  const feedRef = useRef(null);
  const [expandedKey, setExpandedKey] = useState(null);

  const rowKey = (r) => `${r.ts}::${r.action}::${(r.summary || "").slice(0, 60)}`;

  const load = async () => {
    if (paused) return;
    try {
      const { data } = await api.get(`/oauth/live-debug?minutes=${minutes}&limit=500`);
      const incoming = data.rows || [];
      // De-dup — /live-debug returns a rolling window, so we filter to
      // rows we haven't seen yet and append them in chronological order.
      const fresh = incoming.filter((r) => !seenIds.current.has(rowKey(r)));
      if (fresh.length === 0) return;
      fresh.forEach((r) => seenIds.current.add(rowKey(r)));
      setRows((cur) => [...cur, ...fresh].slice(-1000));
    } catch (e) {
      /* silent — endpoint may not exist yet in older deploys */
    }
  };

  useEffect(() => {
    // Seed with the initial window.
    (async () => {
      try {
        const { data } = await api.get(`/oauth/live-debug?minutes=${minutes}&limit=500`);
        const initial = data.rows || [];
        initial.forEach((r) => seenIds.current.add(rowKey(r)));
        setRows(initial);
      } catch (e) { /* ignore */ }
    })();
  }, [minutes]);

  useEffect(() => {
    if (paused) return undefined;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [paused, minutes]);

  useEffect(() => {
    if (!autoScroll || !feedRef.current) return;
    feedRef.current.scrollTop = feedRef.current.scrollHeight;
  }, [rows, autoScroll]);

  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const base = errorsOnly
      ? rows.filter((r) => r.level === "error" || r.level === "warning" || r.level === "critical"
                           || r.status === "error" || r.status === "denied")
      : rows;
    if (!f) return base;
    return base.filter((r) => {
      const bag = [
        r.summary, r.action, r.user_email, r.user_id, r.client_id,
        r.method, r.path, JSON.stringify(r.context || {}),
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(f);
    });
  }, [rows, filter, errorsOnly]);

  const clearAll = () => {
    setRows([]);
    seenIds.current = new Set();
  };

  return (
    <div data-testid="oauth-tracer-panel">
      <ProbeBar />
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <button
          data-testid="live-debug-pause"
          onClick={() => setPaused((p) => !p)}
          className={`inline-flex items-center gap-1 rounded border px-2.5 py-1 text-xs font-medium ${paused ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-slate-300 bg-white text-slate-700"}`}
        >
          {paused ? <><Play className="h-3.5 w-3.5" /> Resume</> : <><Pause className="h-3.5 w-3.5" /> Pause</>}
        </button>
        <button
          data-testid="live-debug-clear"
          onClick={clearAll}
          className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700"
        >
          <Trash2 className="h-3.5 w-3.5" /> Clear
        </button>
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={autoScroll} onChange={(e) => setAutoScroll(e.target.checked)} data-testid="live-debug-autoscroll" />
          <ArrowDownCircle className="h-3.5 w-3.5" /> auto-scroll
        </label>
        <label className="inline-flex items-center gap-1 text-xs text-slate-600">
          <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} data-testid="live-debug-errors-only" />
          errors only
        </label>
        <input
          data-testid="live-debug-filter"
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Filter by user, action, path, summary…"
          className="min-w-64 flex-1 rounded border border-slate-300 bg-white px-2.5 py-1.5 text-sm"
        />
        <select
          data-testid="live-debug-window"
          value={minutes}
          onChange={(e) => setMinutes(parseInt(e.target.value, 10))}
          className="rounded border border-slate-300 bg-white px-2 py-1.5 text-sm"
        >
          <option value={5}>Last 5m</option>
          <option value={15}>Last 15m</option>
          <option value={30}>Last 30m</option>
          <option value={60}>Last 1h</option>
          <option value={240}>Last 4h</option>
          <option value={1440}>Last 24h</option>
        </select>
        <div className="text-xs text-slate-500" data-testid="live-debug-count">
          {filtered.length} of {rows.length} rows
          {paused && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">PAUSED</span>}
        </div>
      </div>

      <div
        ref={feedRef}
        className="h-[70vh] overflow-y-auto rounded-lg border border-slate-200 bg-white font-mono text-[12px]"
        data-testid="live-debug-feed"
      >
        {filtered.length === 0 ? (
          <div className="p-8 text-center text-slate-500">
            No OAuth traffic in the last {minutes} minutes. Launch any Conduit-linked app (Training, Quotes, …) to see events roll in.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((r, idx) => {
              const k = rowKey(r) + "::" + idx;
              const isOpen = expandedKey === k;
              return (
                <li key={k} className={`px-3 py-1.5 hover:bg-slate-50 ${r.level === "error" ? "bg-red-50/70" : r.level === "warning" ? "bg-amber-50/60" : ""}`} data-testid="live-debug-row">
                  <button
                    onClick={() => setExpandedKey(isOpen ? null : k)}
                    className="flex w-full items-center gap-2 text-left"
                  >
                    <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    <TimestampCol ts={r.ts} />
                    <span className={levelPill(r.level)}>{r.level}</span>
                    <span className={`font-semibold ${actionColor(r.action)} min-w-[130px]`}>
                      {actionArrow(r.action)}
                    </span>
                    <span className="min-w-[180px] truncate text-slate-600">
                      {r.user_email || r.user_id || <span className="text-slate-400">—</span>}
                    </span>
                    <span className="min-w-[140px] truncate text-slate-500">
                      {r.client_id || <span className="text-slate-300">—</span>}
                    </span>
                    <span className="flex-1 truncate text-slate-800">{r.summary || ""}</span>
                  </button>
                  {isOpen && <ExpandedRow row={r} />}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="mt-3 text-[11px] text-slate-500">
        Backend: <code>GET /api/oauth/live-debug</code> · Source: <code>system_logs</code> where <code>source_kind = &quot;oauth&quot;</code> · polls every {POLL_MS / 1000}s while expanded
      </div>
    </div>
  );
}

export default function OAuthLiveDebug() {
  return (
    <div data-testid="oauth-live-debug-page">
      <PageHeader
        testid="oauth-tracer-header"
        title="OAuth Tracer"
        titleAccessory={<Activity className="ml-2 h-5 w-5 text-cyan-600" />}
        subtitle={`Live wire-level view of every OAuth/OIDC request & response across all Conduit-linked apps · includes browser-side iframe outcomes · tokens & cookies auto-redacted · polls every ${POLL_MS / 1000}s`}
      />
      <OAuthTracerPanel />
    </div>
  );
}
